/**
 * 現金出納帳管理画面
 * 日々の現金管理・銀行入出金管理
 * Created: 2025-10-08
 */

import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  DatePicker,
  Select,
  InputNumber,
  message,
  Space,
  Tag,
  Statistic,
  Row,
  Col,
  Alert
} from 'antd';
import {
  PlusOutlined,
  BankOutlined,
  SyncOutlined,
  WalletOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined
} from '@ant-design/icons';
import apiClient from '../../utils/apiClient';
import dayjs from 'dayjs';

const { Option } = Select;
const { TextArea } = Input;

interface CashBookEntry {
  id: number;
  transaction_date: string;
  transaction_type: string;
  category: string;
  description: string;
  deposit_amount: number;
  withdrawal_amount: number;
  balance: number;
  reference_type?: string;
  reference_id?: number;
  is_reconciled: boolean;
  created_at: string;
}

interface Category {
  id: number;
  category_name: string;
  category_type: string;
  description: string;
  sort_order: number;
}

const CashBookManagement: React.FC = () => {
  const [entries, setEntries] = useState<CashBookEntry[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [currentBalance, setCurrentBalance] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingEntry, setEditingEntry] = useState<CashBookEntry | null>(null);
  const [form] = Form.useForm();

  const [filterStartDate, setFilterStartDate] = useState(dayjs().startOf('month'));
  const [filterEndDate, setFilterEndDate] = useState(dayjs().endOf('month'));

  useEffect(() => {
    fetchCategories();
    fetchEntries();
  }, [filterStartDate, filterEndDate]);

  const fetchCategories = async () => {
    try {
      const response = await apiClient.get('/cash-book/categories');
      setCategories(response.data);
    } catch (error) {
      console.error('カテゴリー取得エラー:', error);
    }
  };

  const fetchEntries = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/cash-book', {
        params: {
          startDate: filterStartDate.format('YYYY-MM-DD'),
          endDate: filterEndDate.format('YYYY-MM-DD')
        }
      });
      setEntries(response.data.entries || []);
      setCurrentBalance(response.data.currentBalance || 0);
    } catch (error) {
      console.error('現金出納帳取得エラー:', error);
      message.error('データの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = (type: 'deposit' | 'withdrawal') => {
    setEditingEntry(null);
    form.resetFields();
    form.setFieldsValue({
      transaction_date: dayjs(),
      transaction_type: type,
      deposit_amount: 0,
      withdrawal_amount: 0
    });
    setModalVisible(true);
  };

  const handleBankDeposit = () => {
    form.resetFields();
    form.setFieldsValue({
      transaction_date: dayjs(),
      transaction_type: 'withdrawal',
      category: '銀行預入',
      description: '売上金を銀行へ預入',
      withdrawal_amount: currentBalance > 0 ? currentBalance : 0
    });
    setModalVisible(true);
  };

  const handleSyncPOS = async () => {
    const today = dayjs().format('YYYY-MM-DD');

    if (!window.confirm(`POS売上を同期しますか？\n\n本日（${today}）のPOSレジ現金売上を現金出納帳に反映します。`)) {
      return;
    }

    try {
      const response = await apiClient.post('/cash-book/sync-pos-sales', {
        date: today
      });

      if (response.data.success) {
        message.success(response.data.message);
        fetchEntries();
      } else {
        message.warning(response.data.message);
      }
    } catch (error) {
      console.error('同期エラー:', error);
      message.error('同期に失敗しました');
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      const data = {
        ...values,
        transaction_date: values.transaction_date.format('YYYY-MM-DD'),
        deposit_amount: parseInt(values.deposit_amount || 0),
        withdrawal_amount: parseInt(values.withdrawal_amount || 0)
      };

      if (editingEntry) {
        await apiClient.put(`/cash-book/${editingEntry.id}`, data);
        message.success('更新しました');
      } else {
        await apiClient.post('/cash-book', data);
        message.success('登録しました');
      }

      setModalVisible(false);
      fetchEntries();
    } catch (error: any) {
      message.error(error.response?.data?.error || '処理に失敗しました');
    }
  };

  const handleDelete = async (id: number) => {
    Modal.confirm({
      title: '削除しますか？',
      content: '削除すると後続の残高が再計算されます。',
      onOk: async () => {
        try {
          await apiClient.delete(`/cash-book/${id}`);
          message.success('削除しました');
          fetchEntries();
        } catch (error) {
          message.error('削除に失敗しました');
        }
      }
    });
  };

  const columns = [
    {
      title: '日付',
      dataIndex: 'transaction_date',
      key: 'transaction_date',
      width: 120,
      render: (date: string) => dayjs(date).format('MM/DD(ddd)'),
      sorter: (a: CashBookEntry, b: CashBookEntry) =>
        dayjs(a.transaction_date).unix() - dayjs(b.transaction_date).unix()
    },
    {
      title: 'カテゴリー',
      dataIndex: 'category',
      key: 'category',
      width: 120,
      render: (category: string) => {
        const colorMap: { [key: string]: string } = {
          '売上入金': 'green',
          '銀行預入': 'blue',
          '銀行引出': 'cyan',
          '両替': 'orange',
          '仕入支払': 'red',
          '経費支払': 'magenta'
        };
        return <Tag color={colorMap[category] || 'default'}>{category}</Tag>;
      }
    },
    {
      title: '摘要',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true
    },
    {
      title: '入金',
      dataIndex: 'deposit_amount',
      key: 'deposit_amount',
      width: 120,
      align: 'right' as const,
      render: (amount: number) =>
        amount > 0 ? <span style={{ color: 'green' }}>+¥{amount.toLocaleString()}</span> : '-'
    },
    {
      title: '出金',
      dataIndex: 'withdrawal_amount',
      key: 'withdrawal_amount',
      width: 120,
      align: 'right' as const,
      render: (amount: number) =>
        amount > 0 ? <span style={{ color: 'red' }}>-¥{amount.toLocaleString()}</span> : '-'
    },
    {
      title: '残高',
      dataIndex: 'balance',
      key: 'balance',
      width: 120,
      align: 'right' as const,
      render: (balance: number) => (
        <strong style={{ color: balance < 0 ? 'red' : 'inherit' }}>
          ¥{balance.toLocaleString()}
        </strong>
      )
    },
    {
      title: '操作',
      key: 'action',
      width: 100,
      render: (_: any, record: CashBookEntry) => (
        <Button
          type="link"
          danger
          size="small"
          onClick={() => handleDelete(record.id)}
        >
          削除
        </Button>
      )
    }
  ];

  // 期間内の集計
  const periodSummary = entries.reduce((acc, entry) => {
    acc.deposits += entry.deposit_amount;
    acc.withdrawals += entry.withdrawal_amount;
    return acc;
  }, { deposits: 0, withdrawals: 0 });

  return (
    <div style={{ padding: 24 }}>
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="現在残高"
              value={currentBalance}
              prefix="¥"
              valueStyle={{ color: currentBalance < 0 ? '#cf1322' : '#3f8600' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="期間入金合計"
              value={periodSummary.deposits}
              prefix="¥"
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="期間出金合計"
              value={periodSummary.withdrawals}
              prefix="¥"
              valueStyle={{ color: '#cf1322' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="期間収支"
              value={periodSummary.deposits - periodSummary.withdrawals}
              prefix="¥"
              valueStyle={{
                color: (periodSummary.deposits - periodSummary.withdrawals) < 0 ? '#cf1322' : '#3f8600'
              }}
            />
          </Card>
        </Col>
      </Row>

      {currentBalance < 0 && (
        <Alert
          message="残高がマイナスです"
          description="現金残高がマイナスになっています。記帳内容を確認してください。"
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <Card
        title={
          <Space>
            <WalletOutlined />
            <span>現金出納帳</span>
            <DatePicker.RangePicker
              value={[filterStartDate, filterEndDate]}
              onChange={(dates) => {
                if (dates && dates[0] && dates[1]) {
                  setFilterStartDate(dates[0]);
                  setFilterEndDate(dates[1]);
                }
              }}
            />
          </Space>
        }
        extra={
          <Space>
            <Button
              icon={<SyncOutlined />}
              onClick={handleSyncPOS}
            >
              POS売上同期
            </Button>
            <Button
              icon={<BankOutlined />}
              onClick={handleBankDeposit}
            >
              銀行預入
            </Button>
            <Button
              type="primary"
              icon={<ArrowDownOutlined />}
              onClick={() => handleAdd('deposit')}
            >
              入金
            </Button>
            <Button
              danger
              icon={<ArrowUpOutlined />}
              onClick={() => handleAdd('withdrawal')}
            >
              出金
            </Button>
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={entries}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 50,
            showTotal: (total) => `全${total}件`
          }}
          summary={() => (
            <Table.Summary.Row style={{ background: '#fafafa' }}>
              <Table.Summary.Cell index={0} colSpan={3}>
                <strong>期間合計</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={3} align="right">
                <strong style={{ color: 'green' }}>
                  +¥{periodSummary.deposits.toLocaleString()}
                </strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={4} align="right">
                <strong style={{ color: 'red' }}>
                  -¥{periodSummary.withdrawals.toLocaleString()}
                </strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={5} align="right">
                <strong>
                  ¥{currentBalance.toLocaleString()}
                </strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={6} />
            </Table.Summary.Row>
          )}
        />
      </Card>

      <Modal
        title={editingEntry ? '編集' : '新規登録'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={() => form.submit()}
        width={600}
        okText="保存"
        cancelText="キャンセル"
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="transaction_date"
            label="日付"
            rules={[{ required: true, message: '日付を選択してください' }]}
          >
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="transaction_type"
            label="種別"
            rules={[{ required: true }]}
          >
            <Select>
              <Option value="deposit">入金</Option>
              <Option value="withdrawal">出金</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="category"
            label="カテゴリー"
            rules={[{ required: true, message: 'カテゴリーを選択してください' }]}
          >
            <Select>
              {categories.map(cat => (
                <Option key={cat.id} value={cat.category_name}>
                  {cat.category_name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="description"
            label="摘要"
            rules={[{ required: true, message: '摘要を入力してください' }]}
          >
            <TextArea rows={2} placeholder="取引内容の詳細" />
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) =>
              prevValues.transaction_type !== currentValues.transaction_type
            }
          >
            {({ getFieldValue }) => {
              const type = getFieldValue('transaction_type');
              return (
                <>
                  {type === 'deposit' && (
                    <Form.Item
                      name="deposit_amount"
                      label="入金額"
                      rules={[{ required: true, message: '入金額を入力してください' }]}
                    >
                      <InputNumber
                        style={{ width: '100%' }}
                        min={1}
                        formatter={value => `¥ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                        parser={value => value!.replace(/¥\s?|(,*)/g, '')}
                      />
                    </Form.Item>
                  )}
                  {type === 'withdrawal' && (
                    <Form.Item
                      name="withdrawal_amount"
                      label="出金額"
                      rules={[{ required: true, message: '出金額を入力してください' }]}
                    >
                      <InputNumber
                        style={{ width: '100%' }}
                        min={1}
                        formatter={value => `¥ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                        parser={value => value!.replace(/¥\s?|(,*)/g, '')}
                      />
                    </Form.Item>
                  )}
                </>
              );
            }}
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default CashBookManagement;